import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faClipboardCheck,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';

import {
  useCancelBarDeliveryMutation,
  useConfirmBarDeliveryMutation,
  useGetBarDeliveryDetailsQuery,
  useLazyGetBarDeliveryPdfQuery,
} from '../../../redux/api/barDeliveryApi';
import FormatDateTime from '../../../components/dateTime/FormatDateTime';
import PdfButton from '../../../components/pdf/PdfButton';
import { useApiErrorToast } from '../../../hooks/useApiError';
import BarDeliveryTypeBadge, {
  BAR_ACTUAL_LABELS,
  BAR_TYPE_LABELS,
} from '../BarDeliveryTypeBadge';

// A line is ready to confirm only once it carries an explicit, non-negative
// integer. Empty fields keep the button blocked — the backend rejects partial
// confirmations, since an omitted line would leave stock stuck in transit.
const isFilled = (line) =>
  Number.isInteger(line.actual_quantity) && line.actual_quantity >= 0;

const BarDeliveryDetails = ({ confirm = false }) => {
  const { deliveryId } = useParams();
  const { data: delivery } = useGetBarDeliveryDetailsQuery(
    deliveryId ?? skipToken
  );

  const [lines, setLines] = useState([]);
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  const [confirmDelivery, { isLoading: isConfirming }] =
    useConfirmBarDeliveryMutation();
  const [cancelDelivery, { isLoading: isCancelling }] =
    useCancelBarDeliveryMutation();
  const [fetchPdf] = useLazyGetBarDeliveryPdfQuery();

  const docLabel = BAR_TYPE_LABELS[delivery?.type] || 'Lieferung';
  const actualLabel = BAR_ACTUAL_LABELS[delivery?.type] || 'gelieferte Menge';

  useEffect(() => {
    if (delivery) {
      setLines(
        delivery.delivery_items.map((item) => ({
          id: item.id,
          // display_name resolves to the article for goods lines and to the
          // Pfandgruppe (plus the Leerkasten label) for empties, which carry no
          // article at all.
          display_name: item.display_name,
          unit_name: item.unit_name,
          planned_quantity: item.planned_quantity,
          actual_quantity: item.actual_quantity,
        }))
      );
    }
  }, [delivery]);

  const openCount = lines.filter((line) => !isFilled(line)).length;

  const copyPlanned = () =>
    setLines((prev) =>
      prev.map((line) => ({
        ...line,
        actual_quantity: line.planned_quantity,
      }))
    );

  const handleConfirm = async () => {
    try {
      await confirmDelivery({
        id: deliveryId,
        items: lines.map((line) => ({
          item_id: line.id,
          actual_quantity: line.actual_quantity,
        })),
      }).unwrap();
      toast.success(`${docLabel} wurde bestätigt!`);
      navigate(`/bar-delivery/${deliveryId}`);
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Bestätigen!');
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        `${docLabel} wirklich stornieren? Die komplette Lieferung wird zurückgebucht.`
      )
    ) {
      return;
    }
    try {
      await cancelDelivery({ id: deliveryId }).unwrap();
      toast.success(`${docLabel} wurde storniert!`);
      navigate('/bar-delivery/list');
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Stornieren!');
    }
  };

  if (!delivery) return null;

  return (
    <>
      <h4>
        Bar-{docLabel} #{delivery.counter}
        <BarDeliveryTypeBadge
          type={delivery.type}
          cancelled={delivery.cancelled}
        />
      </h4>

      <PdfButton
        onFetch={() => fetchPdf(deliveryId).unwrap()}
        filename={`bar-lieferschein-${delivery.counter}.pdf`}
        label='Lieferschein (PDF)'
        variant='primary'
        size={null}
      />
      <PdfButton
        onFetch={() => fetchPdf(deliveryId).unwrap()}
        filename={`bar-lieferschein-${delivery.counter}.pdf`}
        label='Lieferschein drucken'
        icon='print'
        action='print'
        variant='outline-primary'
        size={null}
        className='ms-2'
      />
      {!confirm && !delivery.confirmed && (
        <>
          <button
            className='btn btn-info ms-2'
            onClick={() => navigate(`/bar-delivery/${deliveryId}/confirm`)}
          >
            <FontAwesomeIcon icon={faClipboardCheck} className='me-2' />
            {docLabel} bestätigen
          </button>
          <button
            className='btn btn-outline-danger ms-2'
            onClick={handleCancel}
            disabled={isCancelling}
          >
            <FontAwesomeIcon icon={faBan} className='me-2' />
            {docLabel} stornieren
          </button>
        </>
      )}

      <table className='table table-striped mt-4'>
        <tbody>
          <tr>
            <td>Bar</td>
            <td>
              <b>{delivery.destination_name}</b>
            </td>
          </tr>
          <tr>
            <td>Partner</td>
            <td>{delivery.partner_name}</td>
          </tr>
          <tr>
            <td>angelegt</td>
            <td>
              <FormatDateTime dateTime={delivery.created_at} /> von{' '}
              <b>{delivery.created_by_username}</b>
            </td>
          </tr>
          {delivery.cancelled ? (
            <tr>
              <td>storniert</td>
              <td className='text-danger'>
                <b>STORNIERT</b> —{' '}
                <FormatDateTime dateTime={delivery.cancelled_at} /> von{' '}
                <b>{delivery.cancelled_by_username}</b>
              </td>
            </tr>
          ) : (
            delivery.confirmed && (
              <tr>
                <td>bestätigt</td>
                <td>
                  <FormatDateTime dateTime={delivery.confirmed_at} /> von{' '}
                  <b>{delivery.confirmed_by_username}</b>
                </td>
              </tr>
            )
          )}
          {delivery.comment?.length > 0 && (
            <tr>
              <td>Kommentar/Hinweis</td>
              <td className='text-danger'>{delivery.comment}</td>
            </tr>
          )}
          {delivery.delivery_note_comment?.length > 0 && (
            <tr>
              <td>Lieferschein-Hinweis (gedruckt)</td>
              <td>{delivery.delivery_note_comment}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className='mt-5 text-start'>
        <h4>Positionen</h4>
        {confirm && (
          <button
            className='btn btn-warning'
            onClick={copyPlanned}
            disabled={lines.length === 0}
          >
            <FontAwesomeIcon icon={faCopy} className='me-2' />
            Alle Mengen wie geplant
          </button>
        )}

        <table className='table table-striped mt-4'>
          <thead>
            <tr>
              <th>{delivery.type === 'EMP' ? 'Pfandgruppe' : 'Artikel'}</th>
              <th>Gebinde</th>
              <th>geplante Menge</th>
              {(confirm || delivery.confirmed) && <th>{actualLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td>{line.display_name}</td>
                <td>{line.unit_name}</td>
                <td>{line.planned_quantity}</td>
                {confirm ? (
                  <td>
                    <input
                      className={`form-control${
                        isFilled(line) ? '' : ' is-invalid'
                      }`}
                      type='number'
                      min='0'
                      max={line.planned_quantity}
                      value={line.actual_quantity ?? ''}
                      onChange={(event) => {
                        const raw = event.target.value;
                        // Keep an empty field as '' rather than coercing to 0,
                        // so the confirm button stays blocked until every line
                        // has an explicit quantity.
                        const next =
                          raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                        setLines((prev) =>
                          prev.map((prevLine) =>
                            prevLine.id === line.id
                              ? { ...prevLine, actual_quantity: next }
                              : prevLine
                          )
                        );
                      }}
                    />
                    {isFilled(line) &&
                      line.actual_quantity > line.planned_quantity && (
                        <div className='text-danger small'>
                          mehr als geplant
                        </div>
                      )}
                  </td>
                ) : (
                  delivery.confirmed && <td>{line.actual_quantity}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {confirm && (
          <>
            <button
              className='btn btn-success'
              onClick={handleConfirm}
              disabled={
                isConfirming ||
                openCount > 0 ||
                lines.some((l) => l.actual_quantity > l.planned_quantity)
              }
            >
              {docLabel} bestätigen
            </button>
            {openCount > 0 && (
              <p className='text-danger mt-2 mb-0'>
                <i className='fas fa-exclamation-triangle me-1' />
                Bitte alle Mengen eintragen ({openCount} offen)
              </p>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default BarDeliveryDetails;
